import DoneAllIcon from "@mui/icons-material/DoneAll";
import { Box, Stack, useTheme } from "@mui/material";
import Container from "@mui/material/Container";
import { ProgressButton } from "@qubit/autoparts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { InventoryAddDialog } from "~/features/inventoryAddDialog/InventoryAddDialog";
import { InventoryProductCard } from "~/features/inventoryProductCard/InventoryProductCard";
import { useFcNavigate } from "~/hooks/useFcNavigate";

import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import {
  fetchCycleCountAndBins,
  fetchCycleCountBins,
  postBinCount,
  completeCycleCounts,
  removeCycleCount,
  clearCycleCountsState,
  clearCycleCountsMessage
} from "~/redux/actions/cycleCounts";
import {
  getVariantByVariantId,
  clearSelectedVariant,
  getInventoryReport,
  placeInventoryHold
} from "~/redux/actions/inventory";
import { StoreState } from "~/redux/reducers";

import CycleCountBinTable from "./CycleCountBinTable";
import { CycleCountToolbar } from "./CycleCountToolbar";
import { selectCycleCountBinId } from "./cycleCounts.slice";

const mapStateToProps = (state: StoreState) => ({
  selectedVariant: state.inventory.selectedVariant,
  cycleCount: state.cycleCounts.cycleCount,
  loadingCycleCountBins: state.cycleCounts.loadingCycleCountBins,
  cycleCountInventoryRecords: state.cycleCounts.cycleCountInventoryRecords,
  selectedPortId: state.workstations.sitePortId,
  inventoryReports: state.inventory.inventoryReport,
  initialFrequency: state.cycleCounts.cycleCountFrequency?.frequency
});

const connector = connect(mapStateToProps, {
  getVariantByVariantId,
  fetchCycleCountAndBins,
  fetchCycleCountBins,
  postBinCount,
  completeCycleCounts,
  clearCycleCountsState,
  clearSelectedVariant,
  clearCycleCountsMessage,
  getInventoryReport,
  placeInventoryHold
});

type PropsFromRedux = ConnectedProps<typeof connector>;
type CycleCountInheritedProps = { viewTitle?: ViewNameTranslation };
export type CycleCountProps = PropsFromRedux & CycleCountInheritedProps;

export function CycleCount(props: CycleCountProps) {
  const {
    selectedVariant,
    cycleCount,
    cycleCountInventoryRecords,
    loadingCycleCountBins,
    selectedPortId,
    inventoryReports,
    initialFrequency,
    viewTitle
  } = props;

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { palette } = useTheme();
  const location = useLocation();
  const navigate = useFcNavigate();

  const { setMenuItems, setToolbar } = useNavbar({ viewTitle });

  const cycleCountId = location.pathname.split("/")[2];

  // component did unmount
  useEffect(
    () => () => {
      props.clearSelectedVariant();
      props.clearCycleCountsState();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const selectedCycleCountBinId = useAppSelector(
    (state) => state.cycleCountsSlice.selectedCycleCountBinId
  );

  const [initialFetchComplete, setInitialFetchComplete] =
    useState<boolean>(false);
  const [isAddPanelOpen, setAddPanelOpen] = useState(false);

  // get product info for selected inventory or variant from params (if no inventory records)
  const searchVariantId: Guid | undefined = cycleCount?.variant?.variantId;

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (searchVariantId) {
      void props.getVariantByVariantId(searchVariantId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVariantId]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!initialFetchComplete) {
      // fetch cycle count, then fetch cycle count bins
      void props.fetchCycleCountAndBins(cycleCountId);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
      setInitialFetchComplete(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFetchComplete]);

  // get inventory report for selected variant. Used for total qty/total committed
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (selectedVariant) {
      void props.getInventoryReport(selectedVariant.variantId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant]);

  useEffect(() => {
    if (selectedCycleCountBinId) {
      setToolbar(<CycleCountToolbar />);
    } else {
      setToolbar(null);
    }
  }, [selectedCycleCountBinId, setToolbar]);

  useEffect(() => {
    setMenuItems([
      {
        textContent: t("remove cycle count"),
        trackingLabel: "Remove Cycle Count",
        actionCb: async () => {
          await dispatch(removeCycleCount(cycleCountId));
          dispatch(selectCycleCountBinId(null));
          navigate("/cycle-count");
        }
      }
    ]);
  }, [cycleCountId, dispatch, navigate, setMenuItems, t]);

  const allCycleCountBinsAreVerified =
    !!cycleCount &&
    cycleCountInventoryRecords.reduce((acc, cycleCountBin) => {
      if (cycleCount && cycleCountBin.count) {
        const matchingBinCount = cycleCount.binCounts.find(
          (binCount) => binCount.inventoryId === cycleCountBin.inventoryId
        );
        return (
          !!matchingBinCount &&
          matchingBinCount.count.value === cycleCountBin.count.value &&
          acc
        );
      }
      return acc;
    }, true);

  const showCompletionButton =
    !loadingCycleCountBins && allCycleCountBinsAreVerified;

  return (
    <Container
      maxWidth="xl"
      sx={{
        margin: "15px auto"
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
          gap: {
            xs: 3,
            md: 5
          }
        }}
      >
        <InventoryProductCard
          variantId={selectedVariant?.variantId}
          addClickCb={() => setAddPanelOpen(true)}
          inventoryReports={inventoryReports}
          initialCycleCountFrequency={initialFrequency}
        />

        <Stack
          flexDirection="column"
          justifyContent="space-between"
          gap={2}
          sx={{ height: "100%", width: "100%" }}
        >
          <CycleCountBinTable />

          {showCompletionButton && (
            <ProgressButton
              id="complete-button"
              sx={{ background: palette.success.dark, color: "white" }}
              buttonSize="medium"
              emphasis="high"
              responsive
              fullWidth
              startIcon={<DoneAllIcon />}
              onClick={async () => {
                await props.completeCycleCounts([cycleCountId]);
                navigate("/cycle-count");
                dispatch(selectCycleCountBinId(null));
              }}
            >
              {t("complete cycle count")}
            </ProgressButton>
          )}
        </Stack>

        <InventoryAddDialog
          open={isAddPanelOpen}
          autostorePortId={selectedPortId}
          onClose={() => setAddPanelOpen(false)}
          variant={selectedVariant ?? undefined}
          refreshCb={async () => {
            await props.fetchCycleCountAndBins(cycleCountId);
            if (selectedVariant) {
              await props.getInventoryReport(selectedVariant.variantId);
            }
          }}
        />
      </Box>
    </Container>
  );
}

export default connector(CycleCount);
